// Scroll-triggered entrance animations and animated stat counters.
// Elements already on screen at load are left alone, so nothing flashes.
// Call after the page has loaded: before that, styles may not be applied and
// the browser may not have jumped to a URL's #section yet, so positions are wrong.

export function initReveal(): void {
  const items = Array.from(document.querySelectorAll<HTMLElement>("[data-reveal]"));
  const counters = Array.from(document.querySelectorAll<HTMLElement>("[data-count]"));
  const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduced || !("IntersectionObserver" in window)) return;

  const pending = items.filter((el) => el.getBoundingClientRect().top > window.innerHeight * 0.92);
  pending.forEach((el) => el.classList.add("reveal"));

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add("is-in");
        io.unobserve(entry.target);
      }
    },
    { rootMargin: "0px 0px -8% 0px", threshold: 0.08 },
  );
  pending.forEach((el) => io.observe(el));

  const countIo = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        countUp(entry.target as HTMLElement);
        countIo.unobserve(entry.target);
      }
    },
    { threshold: 0.6 },
  );
  counters.forEach((el) => countIo.observe(el));
}

function countUp(el: HTMLElement): void {
  const to = Number(el.dataset.count ?? 0);
  if (!to) return;
  const duration = 1200;
  const start = performance.now();
  const step = (now: number) => {
    const p = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - p, 3);
    el.textContent = String(Math.round(to * eased));
    if (p < 1) requestAnimationFrame(step);
  };
  el.textContent = "0";
  requestAnimationFrame(step);
}

/** Cursor-following highlight on cards. */
export function initSpotlight(): void {
  if (!matchMedia("(hover: hover)").matches) return;
  document.querySelectorAll<HTMLElement>("[data-spotlight]").forEach((el) => {
    el.addEventListener("pointermove", (e) => {
      const r = el.getBoundingClientRect();
      el.style.setProperty("--mx", `${e.clientX - r.left}px`);
      el.style.setProperty("--my", `${e.clientY - r.top}px`);
    });
  });
}
